'use client';

import { useMemo } from 'react';

import { SmallLotPricingPackaging, UnitPrice } from '@ravago/shared/page-data/models/elements';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

import { CombinedSuggestion } from '../../../utils/quote-pricing/combined-pricing.utils';
import { getQuantitySuggestionHintKey } from '../../../utils/quote-pricing/quantity-suggestions.utils';
import { isMultipleOf } from '../../../utils/quote-pricing/quantity-steps.utils';

interface Props {
   suggestions: CombinedSuggestion[];
   smallLotPackaging: SmallLotPricingPackaging | undefined;
   onSelect: (quantity: number, quoteLineId?: string) => void;
}

interface SuggestionRow {
   quantity: number;
   packagingName: string | undefined;
   unitPrice: UnitPrice;
   total: { amount: number; currency: string };
   quoteLineId?: string;
}

const toRow = (suggestion: CombinedSuggestion, smallLotPackaging: SmallLotPricingPackaging | undefined): SuggestionRow => {
   if (suggestion.kind === 'small-lot') {
      const { discountedPrice } = suggestion.tier;
      return {
         quantity: suggestion.quantity,
         packagingName: smallLotPackaging?.name,
         unitPrice: { price: discountedPrice, unitOfMeasure: smallLotPackaging?.unitOfMeasure ?? '' },
         total: { amount: suggestion.quantity * discountedPrice.amount, currency: discountedPrice.currency },
         quoteLineId: undefined
      };
   }
   const { lineUnitPrice, linePackaging } = suggestion.line;
   return {
      quantity: suggestion.quantity,
      packagingName: linePackaging,
      unitPrice: lineUnitPrice,
      total: { amount: suggestion.quantity * lineUnitPrice.price.amount, currency: lineUnitPrice.price.currency },
      quoteLineId: suggestion.line.lineId
   };
};

const compareSuggestionRows = (a: SuggestionRow, b: SuggestionRow): number => {
   const unitPriceDiff = a.unitPrice.price.amount - b.unitPrice.price.amount;
   if (unitPriceDiff !== 0) {
      return unitPriceDiff;
   }

   return a.quantity - b.quantity;
};

const isPalletOnlyQuoteSuggestion = (suggestion: CombinedSuggestion): boolean => {
   if (suggestion.kind !== 'quote') return false;

   const palletSize = suggestion.line.linePalletSizeAmount;
   if (!palletSize || palletSize <= 0) return false;

   return suggestion.quantity >= palletSize && isMultipleOf(suggestion.quantity, palletSize);
};

export const QuantitySuggestions = ({ suggestions, smallLotPackaging, onSelect }: Props) => {
   const translate = useTranslations();
   const formatter = useFormatter();

   const rows = useMemo(
      () =>
         suggestions
            .map((suggestion) => toRow(suggestion, smallLotPackaging))
            .sort(compareSuggestionRows),
      [suggestions, smallLotPackaging]
   );

   if (rows.length === 0) return null;

   const isPalletSuggestion = suggestions.every(isPalletOnlyQuoteSuggestion);
   const hasSameQuantityQuoteAlternatives =
      suggestions.length > 1 && suggestions.every((suggestion) => suggestion.kind === 'quote') && new Set(suggestions.map((suggestion) => suggestion.quantity)).size === 1;
   const hintKey = getQuantitySuggestionHintKey(rows.length, isPalletSuggestion);

   return (
      <Box direction="column" gap="xs" spacing="md" backgroundColor="surface-default" border borderRadius="largest">
         {!hasSameQuantityQuoteAlternatives && (
            <Typography variant="small" color="subtle">
               {translate(hintKey)}
            </Typography>
         )}
         {rows.map((row) => (
            <Box
               key={`${row.quantity}-${row.unitPrice.price.amount}-${row.quoteLineId ?? 'small-lot'}`}
               direction="row"
               items="center"
               spacing="sm"
               background="white"
               enableHoverState
               border
               borderRadius="largest"
               gap="sm"
               onClick={() => onSelect(row.quantity, row.quoteLineId)}
            >
               <Typography variant="small-highlight">
                  {formatter.quantity({ amount: row.quantity, unitOfMeasure: row.unitPrice.unitOfMeasure })}
               </Typography>
               {row.packagingName && (
                  <Typography variant="small" color="subtle">
                     {row.packagingName}
                  </Typography>
               )}
               <Box direction="row" gap="sm" items="center" justify="end" flex="1">
                  <Typography variant="small">
                     {formatter.price(row.total, { minimumFractionDigits: 2, maximumFractionDigits: 3 })}
                  </Typography>
                  <Typography variant="small-highlight">{formatter.unitPrice(row.unitPrice)}</Typography>
               </Box>
            </Box>
         ))}
      </Box>
   );
};
